// The console's support, staff and audit screens (spec 15 §4 S10, S17, S18,
// and the tenant-side S19 banner; M15-8, M15-9; D-620 to D-623), English.
// Spread into `adminEn` under three keys, so the area stays one namespace
// and the parity gate covers these keys with the rest. The other five
// locales live beside this file as admin-ops.<locale>.ts.
//
// Counted nouns are never agreed with their number: the count follows a
// colon or an abbreviation (D-030).

export const adminOpsEn = {
  support: {
    tabOverview: "Overview",
    tabSupport: "Support",
    tabsLabel: "Tenant sections",
    title: "Support",
    lead: "Help this tenant's people: open the tenant as one of them, send a reset link, or sign them out.",
    viewerNote:
      "Platform support and platform admins use these tools. You can read the access history below.",
    seeAudit: "Audit log for this tenant",
    people: "People",
    search: "Search",
    searchPlaceholder: "Name or email",
    loading: "Loading people…",
    empty: "No one in this tenant matches.",
    emptyAll: "This tenant has no people yet.",
    loadFailed: "Couldn't load this tenant's people.",
    colName: "Name",
    colEmail: "Email",
    colRoles: "Roles",
    colLastLogin: "Last sign-in",
    never: "Never",
    locked: "Locked",
    actionsFor: "Actions for {{name}}",
    impersonate: "Open as this person",
    sendReset: "Send password reset",
    revokeSessions: "Sign out everywhere",
    resetSent: "A password reset link is on its way to {{email}}.",
    resetNotEligible: {
      email_unverified:
        "{{email}} hasn't confirmed their email address, so no reset link can be sent.",
      user_not_active: "This person's account isn't active, so no reset link can be sent.",
      not_a_person: "This account isn't a person's, so it has no password to reset.",
    },
    revoked: "{{name}} is signed out of this tenant. Sessions ended: {{count}}",
    revokeTitle: "Sign {{name}} out everywhere?",
    revokeBody:
      "Every session {{name}} has in this tenant ends at its next request. They can sign in again.",
    revokeConfirm: "Sign out",
    cancel: "Cancel",
    close: "Close",
    grantTitle: "Open the tenant as {{name}}",
    mode: "Access",
    modes: {
      read_only: "Read only",
      act_as: "Act as this person",
    },
    modeHelp: {
      read_only: "You see what they see. Every change is refused.",
      act_as: "You can make changes as this person. Each change is recorded under your name.",
    },
    approvalNote:
      "Acting as someone needs a platform admin's approval before you can open the tenant.",
    reason: "Reason",
    reasonHint: "At least 10 characters. It's kept with the access record.",
    ticket: "Ticket",
    ticketPlaceholder: "e.g. SUP-341",
    duration: "Length",
    durationOption: "{{minutes}} min",
    request: "Request access",
    requesting: "Requesting…",
    pending: "Waiting for a platform admin to approve.",
    granted: "Access granted. Open the tenant when you're ready.",
    open: "Open tenant",
    opening: "Opening…",
    openNote: "The tenant opens in a new tab. The link works once, for two minutes.",
    liveTitle: "Live access",
    liveEmpty: "No one from Assessly has live access to this tenant.",
    historyTitle: "Support access history",
    historyEmpty: "No one from Assessly has opened this tenant yet.",
    historyMore: "Show older",
    historyFailed: "Couldn't load the access history.",
    colStaff: "Staff",
    colPerson: "Person",
    colMode: "Access",
    colTicket: "Ticket",
    colStatus: "Status",
    colStarted: "Started",
    colLeft: "Time left",
    colChanges: "Changes made",
    minutesLeft: "{{minutes}} min",
    grantStatus: {
      pending_approval: "Waiting for approval",
      active: "Live",
      ended: "Ended",
      expired: "Expired",
      revoked: "Revoked",
    },
    approve: "Approve",
    approving: "Approving…",
    end: "End",
    ending: "Ending…",
    unknownPerson: "No longer in the tenant",
    dangerTitle: "Security incident",
    dangerBody:
      "Signs everyone out of this tenant at once and revokes every live access by Assessly staff.",
    revokeAll: "Sign everyone out",
    revokeAllTitle: "Sign everyone out of {{tenant}}?",
    revokeAllBody:
      "Every session in this tenant ends at its next request, and every live access by Assessly staff is revoked. People can sign in again.",
    revokeAllConfirmLabel: "Type {{subdomain}} to confirm",
    revokeAllDone:
      "Everyone is signed out. Sessions ended: {{sessions}}. Access revoked: {{grants}}.",
    failure: {
      session: "Your session has expired. Sign in again to continue.",
      scope: "Sandbox staff can view this tenant but not change it.",
      role: "Your role can't do this.",
      last_admin: "This is the last active platform admin. Make someone else an admin first.",
      self: "You can't change your own account here.",
      state: "Someone changed this first. The list has been read again.",
      not_eligible: "No reset link can be sent to this person.",
      confirmation: "That isn't this tenant's subdomain.",
      already_accepted: "They've already accepted their invite.",
      grant_live: "You already have live access to this tenant. End it first.",
      not_found: "That person or record is no longer here.",
      failed: "That didn't work. Try again.",
    },
  },

  // S19: the banner the tenant app shows under an impersonation session, and
  // the page the console's one-time link lands on.
  banner: {
    viewing: "{{name}} from Assessly support is using this account",
    viewingUnnamed: "Assessly support is using this account",
    ticket: "Ticket {{ticket}}",
    mode: {
      read_only: "Read only",
      act_as: "Can make changes",
    },
    left: "{{minutes}} min left",
    end: "End session",
    ending: "Ending…",
    endFailed: "Couldn't end the session. Try again.",
    landingTitle: "Opening the support session…",
    landingFailedTitle: "This support link can't be used",
    landingFailedBody:
      "A support link works once, for two minutes, on its own tenant. Open the tenant again from the console.",
    landingMissing: "This page needs the link from the console.",
    endedTitle: "Support session ended",
    endedBody: "This browser is signed out of the tenant.",
    goSignIn: "Go to sign in",
  },

  staffAdmin: {
    title: "Staff",
    lead: "Everyone who can sign in to this console.",
    loading: "Loading staff…",
    loadFailed: "Couldn't load the staff list.",
    readOnlyNote: "Only a platform admin can change staff.",
    forbiddenTitle: "Your role can't see the staff list",
    forbiddenBody: "Platform admins and read-only staff can.",
    invite: "Invite staff",
    inviteTitle: "Invite a staff member",
    email: "Email",
    name: "Name",
    role: "Role",
    scope: "Reach",
    scopes: {
      fleet: "Every tenant",
      sandbox: "Sandbox tenants only",
    },
    inviteSend: "Send invite",
    inviting: "Sending…",
    invited: "An invite is on its way to {{email}}.",
    colName: "Name",
    colRole: "Role",
    colStatus: "Status",
    colTwoFactor: "Two-factor",
    colLastLogin: "Last sign-in",
    never: "Never",
    you: "You",
    sandbox: "Sandbox",
    status: {
      invited: "Invited",
      totp_pending: "Setting up two-factor",
      active: "Active",
      disabled: "Disabled",
    },
    twoFactorOn: "On",
    twoFactorOff: "Not set up",
    actionsFor: "Actions for {{name}}",
    changeRole: "Change role",
    disable: "Disable",
    enable: "Re-enable",
    resendInvite: "Re-send invite",
    resetTotp: "Reset two-factor",
    roleTitle: "Change {{name}}'s role",
    roleSave: "Save role",
    disableTitle: "Disable {{name}}?",
    disableBody:
      "They're signed out at once and any live tenant access ends. Re-enabling sends them a new invite.",
    enableTitle: "Re-enable {{name}}?",
    enableBody:
      "We'll email them a new invite. They set a new password and two-factor before signing in again.",
    resetTitle: "Reset {{name}}'s two-factor sign-in",
    resetBody:
      "They're signed out at once and set up two-factor again at their next sign-in. Use this when they've lost their phone and their recovery codes.",
    resetReason: "Reason",
    resetReasonHint: "At least 5 characters, for example the ticket.",
    resetConfirmLabel: "Type {{email}} to confirm",
    working: "Saving…",
    roleChanged: "{{name}}'s role is now: {{role}}",
    disabled: "{{name}} is disabled.",
    enabled: "{{name}} is re-enabled. A new invite is on its way to {{email}}.",
    resent: "A new invite is on its way to {{email}}.",
    reset: "{{name}} will set up two-factor at their next sign-in.",
    exists: "Someone with that email is already staff.",
    sandboxNotAllowed: "Sandbox staff can't be invited here.",
    lowCodesTitle: "Recovery codes left: {{count}}",
    lowCodesBody: "Make a new set now, while you still have your authenticator.",
    lowCodesAction: "Make new codes",
  },

  audit: {
    title: "Audit log",
    lead: "Every change made from this console, in order. Entries can't be edited.",
    loading: "Loading the audit log…",
    loadFailed: "Couldn't load the audit log.",
    forbiddenTitle: "Your role can't see the audit log",
    forbiddenBody: "Platform admins and read-only staff can.",
    empty: "Nothing matches these filters.",
    emptyAll: "Nothing has been recorded yet.",
    staff: "Staff member",
    anyStaff: "Anyone",
    action: "Action starts with",
    actionPlaceholder: "e.g. staff. or tenant.",
    actionInvalid: "Use lower-case letters, digits, dots and underscores.",
    from: "From",
    to: "To",
    clear: "Clear filters",
    tenantFilter: "Tenant: {{name}}",
    removeTenant: "Remove the tenant filter",
    export: "Export NDJSON",
    exporting: "Exporting…",
    exportFailed: "Couldn't export the audit log.",
    colWhen: "When",
    colWho: "Who",
    colAction: "Action",
    colTenant: "Tenant",
    filterTenant: "Only this tenant",
    entry: "Entry #{{id}}",
    details: "Details for entry #{{id}}",
    before: "Before",
    after: "After",
    metadata: "Details",
    nothing: "Nothing recorded",
    automatic: "Automatically",
    platformStaff: "Platform staff",
    noTenant: "Platform",
    more: "Show older",
    chainTitle: "Chain check",
    verdict: {
      intact: "Intact",
      broken: "Broken",
      never_verified: "Not checked",
    },
    neverVerified: "The chain hasn't been checked yet.",
    intact: "Intact through entry #{{id}}. Checked {{when}}.",
    broken: "Broken at entry #{{id}}: {{reason}}. Checked {{when}}.",
    breakReason: {
      id_gap: "an entry is missing",
      prev_hash_mismatch: "an entry no longer links to the one before it",
      curr_hash_mismatch: "an entry was changed after it was written",
    },
    anchored: "Anchored {{when}}.",
    notAnchored: {
      no_anchor_store: "No anchor store is set up, so the head isn't anchored.",
      chain_broken: "A broken chain is never anchored.",
      put_failed: "The anchor store didn't answer. The next check tries again.",
    },
    verifyNow: "Check now",
    verifying: "Checking…",
    verifyFailed: "The check didn't run. Try again.",
  },

  opsNav: {
    staff: "Staff",
    audit: "Audit log",
  },
} as const;
